import { FC, memo, useState, useRef, useEffect, KeyboardEvent } from 'react';
import classnames from 'classnames';

import { Input } from '@ds-web/components/atoms/form/inputs/input/Input';
import { BorderType } from '@ds-web/tokens/enums';

import { IInlineEditableTextProps } from './types';

import { StyledInlineEditableText } from '@ds-web/components/molecules/ui/inline-editable-text/styles';

/**
 * A text component that appears as regular text but becomes editable on click
 * with hover effects similar to an input field.
 *
 * @interface IInlineEditableTextProps
 * @author Federico Mauri <federico.mauri@shippypro.com>
 */
export const InlineEditableText: FC<IInlineEditableTextProps> = memo(
  ({
    value,
    onChange,
    disabled = false,
    placeholder = '',
    className,
    dataTest,
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setCurrentValue(value);
    }, [value]);

    useEffect(() => {
      if (isEditing && inputRef.current) inputRef.current.focus();
    }, [isEditing]);

    const handleClick = () => {
      if (!disabled) setIsEditing(true);
    };

    const handleSave = () => {
      if (currentValue.trim() === '') handleCancel();
      else if (currentValue.trim() !== value) onChange(currentValue.trim());

      setIsEditing(false);
    };

    const handleCancel = () => {
      setCurrentValue(value);
      setIsEditing(false);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    const handleBlur = () => {
      handleSave();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
    };

    return (
      <StyledInlineEditableText className={className}>
        <div
          className={classnames('editable-text-display', {
            editing: isEditing,
            disabled,
          })}
          onClick={handleClick}
          title={disabled ? undefined : 'Click to edit'}
          data-testid={
            dataTest ? `${dataTest}-display` : 'editable-text-display'
          }
        >
          {currentValue || placeholder}
        </div>

        <Input
          innerRef={inputRef}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={classnames('editable-text-input', { editing: isEditing })}
          borderType={BorderType.default}
          placeholder={placeholder}
          data-testid={dataTest ? `${dataTest}-input` : 'editable-text-input'}
        />
      </StyledInlineEditableText>
    );
  },
);

InlineEditableText.displayName = 'InlineEditableText';
